import { useQueries, useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import React, { memo, ReactNode, useCallback, useMemo } from 'react';
import { MdAdd, MdDeleteOutline } from 'react-icons/md';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Dispatch } from 'redux';
import { ItemDataType } from 'rsuite/lib/@types/common';
import { RootState } from '../../../../../../../@types/RootState';
import { CheckPill, FilterDescription, FilterGroup, FilterLabel, FilterSeparator, SelectPill } from '../../../../../../../components';
import { listProducts, listSegment } from '../../../../../../../components/FilterSection/services';
import { useDebounce } from '../../../../../../../hooks';
import {
  addCacheByKey,
  CacheDataItem,
  decrementRows,
  incrementRows,
  INITIAL_ROWS,
  RegisterLimitStoreCattegoryKeys as Key,
  RegisterLimitStoreCattegoryKeys,
  removeCacheByKey,
  removeSelectedCattegories,
  resetCacheByKey,
  resetValueByIndex,
  resetValueByKey,
  selector,
  setDataByKey,
  setSearchByKey,
  setSegments,
  setSelectedCattegoriesByIndex,
  setValueByKey,
} from '../../../../../../../reducers/ipa/limits/filters/cattegory';
import { checkIfIsLoading } from '../../../../../../../utils';
import { checkIsSegmentEmpty } from '../../lib/utils';
import { listCattegoryData } from '../../pages/RegisterLimitPage/service';
import styles from './CattegoryFilter.module.scss';

type CattegoryFilterProps = RouteComponentProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const CattegoryFilter = memo(
  ({
    segments,
    rows,
    selectedCattegories,
    value,
    data,
    cache,
    search,
    listCattegory,
    removeSelectedCattegories,
    setSelectedCattegoriesByIndex,
    resetValueByKey,
    addCacheByKey,
    removeCacheByKey,
    resetCacheByKey,
    handleResetValueByIndex,
    setDataByKey,
    setSearchByKey,
    setValueByKey,
    incrementRows,
    decrementRows,
  }: CattegoryFilterProps) => {
    const debounce = useDebounce();

    const avaliableCattegories = useMemo(() => segments.filter(segment => !selectedCattegories.includes(segment.value)), [segments, selectedCattegories]);

    const isLastRow = useMemo(() => rows === segments.length, [rows, segments]);

    const isSegmentEmpty = useMemo(() => checkIsSegmentEmpty(value), [value]);

    const handleCleanCacheAndValue = useCallback(
      (key: Key) => {
        return () => {
          resetValueByKey(key);
          resetCacheByKey(key);
        };
      },
      [resetValueByKey, resetCacheByKey],
    );

    const handleClean = useCallback(
      (key: Key) => {
        return () => {
          if (value[key]?.length) handleCleanCacheAndValue(key)();
          removeSelectedCattegories(key);
        };
      },
      [removeSelectedCattegories, handleCleanCacheAndValue, value],
    );

    const handleAddSelectedCattegories = useCallback(
      (index: number) => {
        return (key: Key) => {
          handleResetValueByIndex(index);
          const hasSelectedValues = value[key]?.length;
          if (hasSelectedValues) handleClean(key)();
          setSelectedCattegoriesByIndex(index, key);
        };
      },
      [handleClean, handleResetValueByIndex, setSelectedCattegoriesByIndex, value],
    );

    const listSegmentData = async (key: Key, level: number): Promise<DataItem[]> => {
      if (key === 'productIds') return listProducts(search[key]);
      return listSegment(search[key], level);
    };

    const handleSelectCattegory = useCallback(
      (key: Key) => {
        return (value: string[], item: ItemDataType & { description?: null | string }) => {
          setValueByKey(key, value);

          const isItemAlreadyOnCache = cache[key]?.find(({ value }) => value === item.value);

          if (isItemAlreadyOnCache) removeCacheByKey(key, item.value);
          else addCacheByKey(key, { label: item.label, value: item.value, description: item?.description });
        };
      },
      [addCacheByKey, removeCacheByKey, setValueByKey, cache],
    );

    const handleSearch = useCallback(
      (key: Key) => {
        return (search: string) => {
          debounce(() => setSearchByKey(key, search));
        };
      },
      [setSearchByKey, debounce],
    );

    const handleClose = useCallback(
      (key: Key) => {
        return () => {
          setSearchByKey(key, '');
        };
      },
      [setSearchByKey],
    );

    const handleIncrementRows = useCallback(() => {
      if (isLastRow) return;
      incrementRows();
    }, [isLastRow, incrementRows]);

    const handleDecrementRows = useCallback(
      (key: Key) => {
        return () => {
          if (rows === INITIAL_ROWS) return;
          decrementRows();
          if (key) handleClean(key)();
        };
      },
      [decrementRows, handleClean, rows],
    );

    useQuery({ queryKey: ['filters.cattegory.type'], queryFn: listCattegory });

    const results = useQueries({
      queries: segments.map(({ value: key, level }) => ({
        queryKey: ['segment-data', key, search[key]],
        queryFn: () => listSegmentData(key, level),
        onSuccess: (data: DataItem[]) => setDataByKey(key, _.uniqBy(data, 'value')),
        enabled: selectedCattegories.includes(key),
      })),
    });

    return (
      <FilterGroup asChild>
        <li className={styles['cattegory-row']}>
          <FilterLabel className={styles['cattegory-row__label']}>Categorização</FilterLabel>
          <div className={styles['cattegory-row__wrapper']}>
            {Array.from({ length: rows }).map((__, i) => {
              const key = selectedCattegories[i];
              let currentIndexData = avaliableCattegories;
              const selectedSegmentsData = segments.find(item => item.value === key);
              if (selectedSegmentsData) currentIndexData = [...avaliableCattegories, selectedSegmentsData].sort((a, b) => a.level - b.level);
              const currentValueIndex = value[key];
              const shouldDisplayAddButton = Boolean(!isLastRow && i === rows - 1 && currentValueIndex?.length);
              const isFirstRow = i === 0;
              return (
                <span key={key || i} className={styles['cattegory-row__container']}>
                  <SelectPill
                    name="cattegoryType"
                    data={currentIndexData}
                    value={key}
                    searchable={false}
                    onChange={handleAddSelectedCattegories(i)}
                    onClean={handleClean(key)}
                    renderMenuItem={renderMenuItem}
                  />

                  {key && (
                    <>
                      <FilterSeparator />
                      <CheckPill
                        data={_.uniqBy([...(cache[key] ?? []), ...(data[key] ?? [])], 'value')}
                        value={value[key] ?? []}
                        onSearch={handleSearch(key)}
                        onSelect={handleSelectCattegory(key)}
                        onClose={handleClose(key)}
                        onClean={handleCleanCacheAndValue(key)}
                        isLoading={checkIfIsLoading(results?.[i]?.fetchStatus)}
                      />
                    </>
                  )}

                  {shouldDisplayAddButton && (
                    <button type="button" onClick={handleIncrementRows}>
                      <MdAdd size={20} />
                    </button>
                  )}

                  {!isFirstRow && (
                    <button type="button" onClick={handleDecrementRows(key)}>
                      <MdDeleteOutline size={20} />
                    </button>
                  )}

                  {isFirstRow && isSegmentEmpty && <FilterDescription>Obrigatório, selecione ao menos uma categoria ou produto</FilterDescription>}
                </span>
              );
            })}
          </div>
        </li>
      </FilterGroup>
    );
  },
);

CattegoryFilter.displayName = 'CattegoryFilter';

export const mapStateToProps = (store: RootState) => selector.root(store);

export const mapDispatchToProps = (dispatch: Dispatch) => ({
  listCattegory: async () => {
    const response = await listCattegoryData();
    dispatch(setSegments(response));
    return response;
  },
  setSelectedCattegoriesByIndex: (index: number, cattegory: RegisterLimitStoreCattegoryKeys) => {
    dispatch(setSelectedCattegoriesByIndex({ index, cattegory }));
  },
  removeSelectedCattegories: (key: RegisterLimitStoreCattegoryKeys) => {
    dispatch(removeSelectedCattegories(key));
  },
  resetValueByKey: (key: string) => {
    dispatch(resetValueByKey(key));
  },
  handleResetValueByIndex: (index: number) => {
    dispatch(resetValueByIndex(index));
  },
  addCacheByKey: (key: RegisterLimitStoreCattegoryKeys, item: CacheDataItem) => {
    dispatch(addCacheByKey({ key, item }));
  },
  removeCacheByKey: (key: string, value: DataItem['value']) => {
    dispatch(removeCacheByKey({ key, value }));
  },
  resetCacheByKey: (key: string) => {
    dispatch(resetCacheByKey(key));
  },
  setDataByKey: (key: RegisterLimitStoreCattegoryKeys, data: DataItem[]) => {
    dispatch(setDataByKey({ key, data }));
  },
  setSearchByKey: (key: RegisterLimitStoreCattegoryKeys, search: string) => {
    dispatch(setSearchByKey({ key, search }));
  },
  setValueByKey: (key: RegisterLimitStoreCattegoryKeys, value: string[]) => {
    dispatch(setValueByKey({ key, value }));
  },
  incrementRows: () => {
    dispatch(incrementRows());
  },
  decrementRows: () => {
    dispatch(decrementRows());
  },
});

const renderMenuItem = (label: ReactNode, item: ItemDataType) => {
  return <span className={styles[item.value]}>{label}</span>;
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CattegoryFilter));
